'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { getEntryData } from '@/logic/reward-machine/entry-data';
import { useStore } from '@/store/store';
import { getServerTime } from '@/logic/aggregation/server-time';
import { useGlobalContext } from '@/store/contexts/global-context';
import { getRewardMachineGameVariant } from '@/utils/reward-machine/get-game-variant';
import type { EntryData } from '@/types/reward-machine';
import { useAccountUm } from '@/hooks/use-account-um';
import { mapEntryError, REWARD_MACHINE_ERROR } from '@/logic/reward-machine/map-entry-error';

const ENTRY_THROTTLE_MS = 5000;
const HTTP_UNAUTHORIZED = 401;

type ServerTime = { loading: boolean; time?: string };

export const useRewardMachineEntry = ({
    providedServerTime,
    loggedIn,
    timezone,
    initialEntryData
}: {
    providedServerTime?: ServerTime;
    loggedIn: boolean;
    timezone?: string;
    initialEntryData?: EntryData | null;
}) => {
    const {
        setEntryData,
        entry: entryData,
        entryFetchInFlight,
        entryLastSuccessAt,
        setEntryFetchInFlight,
        setEntryLastSuccessAt
    } = useStore((state) => state.rewardMachine);
    const { experiments } = useGlobalContext();
    const [serverTime, setServerTime] = useState<ServerTime | undefined>(providedServerTime);
    const refreshingTimeRef = useRef(false);
    const forceRefetchRef = useRef(false);
    const { reauthenticate } = useAccountUm({ keepSessionAliveOnActivity: false });

    useEffect(() => {
        if (initialEntryData && !entryData) {
            setEntryData(initialEntryData);
            if (providedServerTime?.time) {
                setEntryLastSuccessAt(new Date(providedServerTime.time).getTime());
            }
        }
    }, [initialEntryData, entryData, setEntryData, providedServerTime, setEntryLastSuccessAt]);

    const fetchEntryData = useCallback(() => {
        const { time, loading } = serverTime ?? {};

        if (!loggedIn || !time || loading || entryFetchInFlight) return;

        const serverTimeMs = new Date(time).getTime();

        if (Number.isNaN(serverTimeMs)) {
            setEntryData({ error: REWARD_MACHINE_ERROR.TIME } as EntryData);
            return;
        }

        const forceRefetch = forceRefetchRef.current;
        if (!forceRefetch && serverTimeMs - entryLastSuccessAt < ENTRY_THROTTLE_MS) return;
        forceRefetchRef.current = false;

        const gameVariant = getRewardMachineGameVariant(experiments);

        setEntryFetchInFlight(true);

        const loadEntryData = async () => {
            const { rewardMachine } = useStore.getState();
            try {
                let data = await getEntryData(time, gameVariant);

                if (data?.status === HTTP_UNAUTHORIZED && !rewardMachine.reauthBlocked) {
                    rewardMachine.setReauthBlocked?.(true);
                    const session = await reauthenticate();
                    if (session) {
                        data = await getEntryData(time, gameVariant);
                    }
                }

                if (data?.status !== HTTP_UNAUTHORIZED) {
                    rewardMachine.setReauthBlocked?.(false);
                }

                setEntryData({ ...data, error: mapEntryError(data) } as EntryData);
            } catch (error) {
                console.error('Rewards Machine Entry call failed:', error);
                setEntryData({ error: REWARD_MACHINE_ERROR.SERVER } as EntryData);
            } finally {
                setEntryLastSuccessAt(serverTimeMs);
                setEntryFetchInFlight(false);
            }
        };

        void loadEntryData();
    }, [
        loggedIn,
        serverTime,
        setEntryData,
        entryFetchInFlight,
        entryLastSuccessAt,
        setEntryFetchInFlight,
        setEntryLastSuccessAt,
        reauthenticate,
        experiments
    ]);

    const triggerEntryCall = useCallback(async () => {
        if (refreshingTimeRef.current) return;
        if (!timezone) {
            console.warn('Timezone is not provided for Rewards Machine Entry call.');
            return;
        }

        refreshingTimeRef.current = true;
        try {
            const time = await getServerTime(timezone);
            forceRefetchRef.current = true;
            setServerTime({ loading: false, time });
        } finally {
            refreshingTimeRef.current = false;
        }
    }, [timezone]);

    useEffect(() => {
        if (initialEntryData) return;
        fetchEntryData();
    }, [fetchEntryData, initialEntryData]);

    return { entryData, fetchEntryData, triggerEntryCall };
};
